import { formatNumber } from '@sortlist-frontend/utils';
import { useState } from 'react';

import type { GuideLabels, GuideSeatPlan } from '../types';
import { BarTrack } from './BarTrack';

interface SeatsComparatorProps {
  label: string;
  unit: string;
  min: number;
  max: number;
  value: number;
  plans: readonly GuideSeatPlan[];
  footnote: string;
  locale: string;
  labels: Pick<GuideLabels, 'perMonth'>;
}

const planCost = (plan: GuideSeatPlan, seats: number) => (plan.per != null ? plan.per * seats : (plan.flat ?? 0));

export const SeatsComparator = (props: SeatsComparatorProps) => {
  const { label, unit, min, max, value, plans, footnote, locale, labels } = props;
  const [seats, setSeats] = useState(value);
  const costs = plans.map((plan) => planCost(plan, seats));
  const highest = Math.max(...costs, 1);

  return (
    <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 md:p-8">
      <label className="block text-sm font-semibold text-secondary-950">
        {label} : <strong aria-live="polite">{seats}</strong>
        <input
          type="range"
          min={min}
          max={max}
          value={seats}
          onChange={(event) => setSeats(Number(event.target.value))}
          className="mt-2 w-full accent-primary-500"
        />
      </label>
      <ul className="mt-6 space-y-3">
        {plans.map((plan, index) => (
          <li key={plan.label}>
            <p className="mb-1 flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="font-semibold text-secondary-950">
                {plan.label}
                {plan.note != null ? <span className="font-normal text-secondary-500"> · {plan.note}</span> : null}
              </span>
              <span className="font-semibold text-secondary-950">
                {formatNumber({ value: costs[index], locale })} {unit} {labels.perMonth}
              </span>
            </p>
            <BarTrack label={plan.label} ratio={costs[index] / highest} />
          </li>
        ))}
      </ul>
      <p className="mt-5 text-xs text-secondary-500">{footnote}</p>
    </div>
  );
};
